import React, { useState, useEffect } from "react";
import upload from "../../assets/images/registration/upload.png";
import { Category } from "../../utils/types";
import { uploadImage } from "../../utils/helpers";
import { subCategoryService } from "../../services";
import { toast } from "react-toastify";
import { useTheme } from "../../context/ThemeContext";

interface DishForm1Props {
  onNext: (data: DishForm1Data) => void;
  onChange?: (data: Partial<DishForm1Data>) => void;
  initialData?: DishForm1Data;
  categories?: Category[];
}

export interface DishForm1Data {
  dishName: string;
  description: string;
  category: string;
  subCategoryId: string;
  shortDescription: string;
  images: Array<{
    url: string;
    isPrimary: boolean;
    sortOrder: number;
  }>;
}

const DishForm1: React.FC<DishForm1Props> = ({
  onNext,
  onChange,
  initialData,
  categories = [],
}) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState<DishForm1Data>({
    dishName: initialData?.dishName || "",
    description: initialData?.description || "",
    category: initialData?.category || "",
    subCategoryId: initialData?.subCategoryId || "",
    shortDescription: initialData?.shortDescription || "",
    images: initialData?.images || [],
  });

  const [error, setError] = useState("");
  const [uploadingPrimary, setUploadingPrimary] = useState(false);
  const [uploadingAdditional, setUploadingAdditional] = useState(false);
  const [subCategories, setSubCategories] = useState<
    Array<{ _id: string; name: string }>
  >([]);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);

  // Update form data when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData) {
      setFormData({
        dishName: initialData.dishName || "",
        description: initialData.description || "",
        category: initialData.category || "",
        subCategoryId: initialData.subCategoryId || "",
        shortDescription: initialData.shortDescription || "",
        images: initialData.images || [],
      });
    }
  }, [initialData]);

  // Fetch subcategories when category changes
  useEffect(() => {
    const fetchSubCategories = async () => {
      if (!formData.category) {
        setSubCategories([]);
        return;
      }

      setLoadingSubCategories(true);
      try {
        const response: any = await subCategoryService.getSubCategories(
          formData.category,
        );
        if (response.status && response.data) {
          setSubCategories(response.data);
        }
      } catch (error: any) {
        console.error("Error fetching subcategories:", error);
        setSubCategories([]);
        toast.error(error.message || "Failed to load subcategories");
      } finally {
        setLoadingSubCategories(false);
      }
    };

    fetchSubCategories();
  }, [formData.category]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    const updatedData = { [name]: value };

    // If category changes, reset subcategory
    if (name === "category") {
      setFormData((prev) => ({
        ...prev,
        category: value,
        subCategoryId: "",
      }));
      if (onChange) {
        onChange({ category: value, subCategoryId: "" });
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        ...updatedData,
      }));
      if (onChange) {
        onChange(updatedData);
      }
    }
    setError("");
  };

  // Handle Primary Image Upload
  const handlePrimaryImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input immediately so it can be used again
    e.target.value = '';

    setUploadingPrimary(true);
    setError("");

    try {
      const imageUrl = await uploadImage(file, "dish", {
        maxSize: 5,
        allowedTypes: ["image/jpeg", "image/jpg", "image/png", "image/webp"],
      });

      console.log("Primary image uploaded:", imageUrl);

      // Remove existing primary image if any and add new one
      const nonPrimaryImages = formData.images.filter((img) => !img.isPrimary);
      const updatedImages = [
        { url: imageUrl, isPrimary: true, sortOrder: 0 },
        ...nonPrimaryImages.map((img, idx) => ({ ...img, sortOrder: idx + 1 })),
      ];

      console.log("Updated images array:", updatedImages);

      setFormData((prev) => ({
        ...prev,
        images: updatedImages,
      }));

      // Call onChange to update parent state
      if (onChange) {
        onChange({ images: updatedImages });
        console.log("Called onChange with images:", updatedImages);
      }
    } catch (error: any) {
      setError(error.message || "Failed to upload primary image");
      toast.error(error.message || "Failed to upload primary image");
    } finally {
      setUploadingPrimary(false);
    }
  };

  // Handle Additional Images Upload
  const handleAdditionalImagesUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Check if we can add more images (max 5 additional + 1 primary = 6 total)
    const currentAdditionalCount = formData.images.filter(
      (img) => !img.isPrimary,
    ).length;
    const remainingSlots = 5 - currentAdditionalCount;

    if (remainingSlots <= 0) {
      setError("Maximum 5 additional images allowed");
      return;
    }

    const filesToUpload = Array.from(files).slice(0, remainingSlots);
    setUploadingAdditional(true);
    setError("");

    try {
      const uploadPromises = filesToUpload.map((file) =>
        uploadImage(file, "dish", {
          maxSize: 5,
          allowedTypes: ["image/jpeg", "image/jpg", "image/png", "image/webp"],
        }),
      );

      const imageUrls = await Promise.all(uploadPromises);

      const primaryImage = formData.images.find((img) => img.isPrimary);
      const existingAdditional = formData.images.filter(
        (img) => !img.isPrimary,
      );

      const newAdditionalImages = imageUrls.map((url, idx) => ({
        url,
        isPrimary: false,
        sortOrder: existingAdditional.length + idx + 1,
      }));

      const updatedImages = [
        ...(primaryImage ? [primaryImage] : []),
        ...existingAdditional,
        ...newAdditionalImages,
      ];

      console.log("Additional images uploaded:", imageUrls);
      console.log("Updated images array:", updatedImages);

      setFormData((prev) => ({
        ...prev,
        images: updatedImages,
      }));

      // Call onChange to update parent state
      if (onChange) {
        onChange({ images: updatedImages });
        console.log("Called onChange with images:", updatedImages);
      }
    } catch (error: any) {
      setError(error.message || "Failed to upload additional images");
      toast.error(error.message || "Failed to upload primary image");
    } finally {
      setUploadingAdditional(false);
    }
  };

  const handleRemoveLogo = () => {
    // Remove only the primary image (index 0), keep additional images
    const updatedImages = formData.images.filter((img) => !img.isPrimary);
    
    setFormData((prev) => ({
      ...prev,
      images: updatedImages,
    }));
    
    // Call onChange to update parent state
    if (onChange) {
      onChange({ images: updatedImages });
    }
    
    toast.info("Primary image removed");
  };

  // Get primary and additional images for display
  const primaryImage = formData.images.find((img) => img.isPrimary);
  const additionalImages = formData.images.filter((img) => !img.isPrimary);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!formData.dishName.trim()) {
      setError("Item name is required");
      return;
    }
    if (!formData.description.trim()) {
      setError("Item description is required");
      return;
    }
    if (!formData.category) {
      setError("Please select a category");
      return;
    }
    if (!formData.subCategoryId) {
      setError("Please select a subcategory");
      return;
    }
    if (!primaryImage) {
      setError("Please upload a primary image");
      return;
    }
    if (!formData.shortDescription.trim()) {
      setError("Short description is required");
      return;
    }

    onNext(formData);
  };

  return (
    <div className="w-full rounded-lg px-3 sm:px-4 md:px-6 pb-20">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Item Name */}
        <div>
          <label className={`block font-Urbanist text-[15px] font-medium mb-2 leading-[140%] ${isDarkMode ? 'text-gray-300' : 'text-[#6A6A6A]'}`}>
            Name of Dish
          </label>
          <input
            type="text"
            name="dishName"
            value={formData.dishName}
            onChange={handleChange}
            placeholder="Masala Dosa"
            className={`w-full px-4 py-3 border rounded-lg font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00] transition-colors ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
        </div>
        {/* Category */}
        <div>
          <label className={`block font-Urbanist text-[15px] font-medium mb-2 leading-[140%] ${isDarkMode ? 'text-gray-300' : 'text-[#6A6A6A]'}`}>
            Category
          </label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-lg font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00] transition-colors ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="" disabled>
              Select category
            </option>
            {categories
              .filter((cat) => cat.isActive)
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((category) => (
                <option key={category._id} value={category._id}>
                  {category.name}
                </option>
              ))}
          </select>
        </div>

        {/*Sub Category */}
        <div>
          <label className={`block font-Urbanist text-[15px] font-medium mb-2 leading-[140%] ${isDarkMode ? 'text-gray-300' : 'text-[#6A6A6A]'}`}>
            Sub Category
          </label>
          <select
            name="subCategoryId"
            value={formData.subCategoryId}
            onChange={handleChange}
            disabled={!formData.category || loadingSubCategories}
            className={`w-full px-4 py-3 border rounded-lg font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00] transition-colors disabled:cursor-not-allowed ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white disabled:bg-gray-800'
                : 'bg-white border-gray-300 text-gray-900 disabled:bg-gray-100'
            }`}
          >
            <option value="" disabled>
              {loadingSubCategories
                ? "Loading subcategories..."
                : !formData.category
                  ? "Select category first"
                  : "Select subcategory"}
            </option>
            {subCategories.map((subCategory) => (
              <option key={subCategory._id} value={subCategory._id}>
                {subCategory.name}
              </option>
            ))}
          </select>
        </div>

        {/* Item Description */}
        <div>
          <label className={`block font-Urbanist text-[15px] font-medium mb-2 leading-[140%] ${isDarkMode ? 'text-gray-300' : 'text-[#6A6A6A]'}`}>
            Description of Dish
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Max 500 characters."
            rows={4}
            className={`w-full px-4 py-3 border rounded-lg font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00] transition-colors resize-none ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
        </div>

        {/* Primary Image */}
        <div className="flex flex-col gap-2">
          <label className={`block font-Urbanist text-[15px] font-medium mb-2 leading-[140%] ${isDarkMode ? 'text-gray-300' : 'text-[#6A6A6A]'}`}>
            Primary Image
          </label>
          <div className="relative">
            <input
              type="file"
              id="primaryImage"
              accept="image/*"
              onChange={handlePrimaryImageUpload}
              className="hidden"
              disabled={uploadingPrimary}
            />
            <label
              htmlFor="primaryImage"
              className={`w-full px-4 py-3 text-sm border rounded-lg transition-all cursor-pointer flex items-center justify-between ${
                isDarkMode
                  ? 'border-gray-600 bg-gray-700 hover:bg-gray-600 focus-within:border-orange-500 focus-within:bg-gray-700'
                  : 'border-gray-200 bg-gray-50 hover:bg-gray-100 focus-within:border-orange-500 focus-within:bg-white'
              }`}
            >
              <span
                className={primaryImage ? (isDarkMode ? 'text-gray-200' : 'text-gray-700') : (isDarkMode ? 'text-gray-500' : 'text-gray-400')}
              >
                {uploadingPrimary
                  ? "Uploading..."
                  : primaryImage
                    ? primaryImage.url.split("/").pop()?.substring(0, 40) +
                      "..."
                    : "Upload Primary Photo"}
              </span>
              <img src={upload} alt="Upload" className="w-[30px] h-[30px]" />
            </label>
          </div>
          {primaryImage && (
            <div className="mt-2 relative w-32">
              <img
                src={primaryImage.url}
                alt="Primary"
                className="w-32 h-32 object-cover rounded-lg border-2 border-orange-500"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveLogo();
                }}
                className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-md"
                title="Remove logo"
              >
                ×
              </button>
            </div>
          )}
        </div>

        {/* Additional Images */}
        <div className="flex flex-col gap-2">
          <label className={`block font-Urbanist text-[15px] font-medium mb-2 leading-[140%] ${isDarkMode ? 'text-gray-300' : 'text-[#6A6A6A]'}`}>
            Additional Images
          </label>
          <div className="relative">
            <input
              type="file"
              id="additionalImages"
              accept="image/*"
              multiple
              onChange={handleAdditionalImagesUpload}
              className="hidden"
              disabled={uploadingAdditional}
            />
            <label
              htmlFor="additionalImages"
              className={`w-full px-4 py-3 text-sm border rounded-lg transition-all cursor-pointer flex items-center justify-between ${
                isDarkMode
                  ? 'border-gray-600 bg-gray-700 hover:bg-gray-600 focus-within:border-orange-500 focus-within:bg-gray-700'
                  : 'border-gray-200 bg-gray-50 hover:bg-gray-100 focus-within:border-orange-500 focus-within:bg-white'
              }`}
            >
              <span
                className={
                  additionalImages.length > 0
                    ? (isDarkMode ? 'text-gray-200' : 'text-gray-700')
                    : (isDarkMode ? 'text-gray-500' : 'text-gray-400')
                }
              >
                {uploadingAdditional
                  ? "Uploading..."
                  : additionalImages.length > 0
                    ? `${additionalImages.length} image${additionalImages.length > 1 ? "s" : ""} uploaded`
                    : "Upload Additional Images (Up to 5)"}
              </span>
              <img src={upload} alt="Upload" className="w-[30px] h-[30px]" />
            </label>
          </div>
          {additionalImages.length > 0 && (
            <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
              {additionalImages.map((image, idx) => (
                <div key={idx} className="relative">
                  <img
                    src={image.url}
                    alt={`Additional ${idx + 1}`}
                    className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const updatedImages = formData.images.filter(
                        (img) => img.url !== image.url,
                      );
                      setFormData((prev) => ({
                        ...prev,
                        images: updatedImages,
                      }));
                      // Call onChange to update parent state
                      if (onChange) {
                        onChange({ images: updatedImages });
                        console.log(
                          "Deleted image, updated images:",
                          updatedImages,
                        );
                      }
                    }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Item Short Description */}
        <div>
          <label className={`block font-Urbanist text-[15px] font-medium mb-2 leading-[140%] ${isDarkMode ? 'text-gray-300' : 'text-[#6A6A6A]'}`}>
            Short Description
          </label>
          <textarea
            name="shortDescription"
            value={formData.shortDescription}
            onChange={handleChange}
            placeholder="Max 100 characters."
            rows={4}
            className={`w-full px-4 py-3 border rounded-lg font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00] transition-colors resize-none ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="text-red-500 text-sm font-Urbanist">{error}</div>
        )}

        {/* Next Button */}
        <button
          type="submit"
          className="w-full sm:w-auto sm:min-w-[280px] md:w-[300px] bg-[#FF8A00] hover:bg-[#E67A00] text-white font-Urbanist text-lg sm:text-xl md:text-[25px] font-semibold py-3 sm:py-3.5 rounded-full sm:rounded-[188px] transition-colors sm:float-right leading-[140%]"
        >
          Next
        </button>
      </form>
    </div>
  );
};

export default DishForm1;
